import { type Lot, ShrinkReason } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, newIdempotencyKey } from '../api';
import { StockNav } from '../components/StockNav';
import { Empty, ErrorBox, QueryView, SearchBox } from '../components/ui';
import { formatQty, formatStamp } from '../format';
import { useDebounced, useSearchParam } from '../hooks';
import { useLocale } from '../i18n';
import { useSite } from '../sites';
import { qk } from '../queryClient';
import { parseNumber } from './StockPage';

/** Every lot with stock, oldest first; tap one to record a loss against it with its cause. */
export function LotsPage() {
  const { t, intlLocale } = useLocale();
  const [q, setQ] = useSearchParam('q');
  const term = useDebounced(q.trim().toLowerCase());
  const [open, setOpen] = useState<string | null>(null);
  const { siteId } = useSite();
  const lots = useQuery({ queryKey: qk.lots(`stock:${siteId ?? ''}`), queryFn: ({ signal }) => api.lots(siteId ? { site: siteId } : {}, signal) });

  return (
    <div className="stack">
      <section className="stack-sm">
        <h1>{t('lots')}</h1>
        <StockNav />
        <p className="muted">{t('oldestFirst')}</p>
      </section>
      <div className="quick-grid">
        <Link to="/stock/labels" className="btn btn-secondary">
          {t('labels')}
        </Link>
        <Link to="/stock/trace-export" className="btn btn-secondary">
          {t('traceExport')}
        </Link>
      </div>
      <SearchBox value={q} onChange={setQ} />
      <QueryView query={lots}>
        {(rows) => {
          const shown = term ? rows.filter((l) => l.itemName.toLowerCase().includes(term) || l.lotCode.toLowerCase().includes(term)) : rows;
          return shown.length === 0 ? (
            <Empty>{t('noLots')}</Empty>
          ) : (
            <ul className="list">
              {shown.map((l) => (
                <li key={l.id} className="count-row stack-sm">
                  <button type="button" className="row" aria-expanded={open === l.id} onClick={() => setOpen(open === l.id ? null : l.id)}>
                    <span className="row-main">
                      <span className="row-title">{l.itemName}</span>
                      <span className="row-sub">
                        <Link to={`/stock/trace/${encodeURIComponent(l.lotCode)}`} className="label" onClick={(e: MouseEvent) => e.stopPropagation()}>{l.lotCode}</Link>
                        {formatStamp(l.receivedAt, intlLocale, 'date')} · {l.ageDays} {t('ageDays')}
                        {l.vendorName ? ` · ${l.vendorName}` : ''}
                        {l.grade && l.grade !== 'good' ? <span className="badge badge-warn">{t(`grade_${l.grade}`)}</span> : null}
                      </span>
                    </span>
                    <span className="row-end">
                      <span className="row-end-label">{t('onHand')}</span>
                      <span className="row-end-value">
                        {formatQty(l.onHandPacks)} {l.unit ?? ''}
                      </span>
                    </span>
                  </button>
                  {open === l.id ? <ShrinkForm lot={l} onDone={() => setOpen(null)} /> : null}
                </li>
              ))}
            </ul>
          );
        }}
      </QueryView>
    </div>
  );
}

function ShrinkForm({ lot, onDone }: { lot: Lot; onDone: () => void }) {
  const { t } = useLocale();
  const qc = useQueryClient();
  const [qty, setQty] = useState('');
  const [reason, setReason] = useState<ShrinkReason>('age');
  const [note, setNote] = useState('');
  const attempt = useRef<{ body: string; key: string } | null>(null);
  const save = useMutation({
    mutationFn: (v: { body: Parameters<typeof api.shrink>[0]; key: string }) => api.shrink(v.body, v.key),
    retry: (count, error) => error instanceof ApiError && error.status === 0 && count < 3,
    onSuccess: () => {
      attempt.current = null;
      void qc.invalidateQueries({ queryKey: ['stock'] });
      void qc.invalidateQueries({ queryKey: ['items'] });
      onDone();
    },
    onError: (error) => {
      if (!(error instanceof ApiError && error.status === 0)) attempt.current = null;
    },
  });
  const n = parseNumber(qty);
  function submit() {
    if (n === null || n <= 0) return;
    // more than the lot holds is almost always a slip (10 for 1): said in numbers and asked before it is written
    if (n > lot.onHandPacks + 1e-9 && !window.confirm(t('shrinkOverLot').replaceAll('{n}', formatQty(n)).replaceAll('{have}', formatQty(lot.onHandPacks)).replaceAll('{lot}', lot.lotCode))) return;
    const body = { itemId: lot.itemId, lotId: lot.id, qty: n, reason, ...(note.trim() ? { note: note.trim() } : {}) };
    const serialized = JSON.stringify(body);
    if (attempt.current?.body !== serialized) attempt.current = { body: serialized, key: newIdempotencyKey() };
    save.mutate({ body, key: attempt.current.key });
  }
  return (
    <form
      className="count-open"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <span className="label">{t('recordShrink')}</span>
      <input className="count-input" type="text" inputMode="decimal" autoComplete="off" placeholder="—" aria-label={`${t('recordShrink')}: ${lot.lotCode}`} value={qty} onChange={(e) => setQty(e.target.value)} autoFocus />
      <select value={reason} onChange={(e) => setReason(ShrinkReason.parse(e.target.value))} aria-label={t('shrinkReason')}>
        {ShrinkReason.options.map((r) => (
          <option key={r} value={r}>
            {t(`shrink_${r}`)}
          </option>
        ))}
      </select>
      <input type="text" autoComplete="off" placeholder={t('notes')} aria-label={`${t('notes')}: ${lot.lotCode}`} value={note} onChange={(e) => setNote(e.target.value)} style={{ flex: 1, minWidth: 120 }} />
      <button type="submit" className="btn btn-primary" disabled={save.isPending || n === null || n <= 0}>
        {save.isPending ? t('sending') : t('save')}
      </button>
      <button type="button" className="btn btn-ghost" onClick={onDone}>
        {t('cancel')}
      </button>
      {save.error ? <ErrorBox error={save.error} /> : null}
    </form>
  );
}
